import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Stack,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../store/contexts";
import { getApiAsync } from "../../api/apiClient";
import { graphqlToDateTime } from "../../common/utils";

type TrustedDevice = {
  id: string;
  browserName?: string | null;
  osName?: string | null;
  deviceType?: string | null;
  ipAddress?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
  expiresAt: string;
};

/**
 * Gestión de dispositivos de confianza de 2FA ("recordados"): listar y revocar.
 * Revocar fuerza a que ese dispositivo vuelva a pedir 2FA en el próximo login.
 */
export const TrustedDevicesSection = observer(() => {
  const rootStore = useRootStore();
  const { authStore, langStore, activityStore } = rootStore as any;
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = await getApiAsync(authStore, activityStore);
      const data = await (api as any).MyTrustedDevices();
      setDevices(data.myTrustedDevices ?? []);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [authStore, activityStore]);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (id: string) => {
    const api = await getApiAsync(authStore, activityStore);
    await (api as any).RevokeTrustedDevice({ id });
    await load();
  };

  const revokeAll = async () => {
    const api = await getApiAsync(authStore, activityStore);
    await (api as any).RevokeAllTrustedDevices();
    await load();
  };

  // DD/MM/AAAA y 24 horas, sea cual sea el idioma de la app.
  const fmt = (d?: string | null) => (d ? graphqlToDateTime(d) : "—");

  const deviceLabel = (dev: TrustedDevice) =>
    [dev.browserName, dev.osName].filter(Boolean).join(" · ") ||
    dev.deviceType ||
    (langStore.trustedDeviceUnknown || "Dispositivo");

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {langStore.trustedDevicesTitle || "Dispositivos recordados (2FA)"}
      </Typography>

      {loading ? (
        <CircularProgress size={24} />
      ) : devices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {langStore.trustedDevicesEmpty ||
            "No hay dispositivos recordados. Cada dispositivo pedirá el código 2FA."}
        </Typography>
      ) : (
        <>
          <List dense>
            {devices.map((dev, idx) => (
              <React.Fragment key={dev.id}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    <Button size="small" color="error" onClick={() => void revoke(dev.id)}>
                      {langStore.trustedDeviceRevoke || "Revocar"}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={deviceLabel(dev)}
                    secondary={
                      `${dev.ipAddress ?? ""}  ·  ${langStore.trustedDeviceLastUsed || "Último uso"}: ${fmt(dev.lastUsedAt)}  ·  ${langStore.trustedDeviceExpires || "Expira"}: ${fmt(dev.expiresAt)}`
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
          <Stack direction="row" sx={{ mt: 1 }}>
            <Button size="small" color="error" variant="outlined" onClick={() => void revokeAll()}>
              {langStore.trustedDevicesRevokeAll || "Revocar todos"}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
});
